import { BLOG_DEDUPE_REDIRECTS } from './dedupe-redirects.mjs';
import { REDIRECCIONES_CONSOLIDACION } from './redirecciones-consolidacion.mjs';

/**
 * Une las listas de redirecciones del blog y resuelve las cadenas, para que
 * cada slug retirado llegue a su destino final en un único salto.
 *
 * Las dos listas son ficheros derivados de procesos distintos (deduplicación
 * de agosto y consolidación de v1.8), así que un destino de una puede ser el
 * origen de la otra: `el-sindrome-del-domingo-…` apuntaba a un post que la
 * consolidación fusiona. Resolverlo aquí evita editar a mano un fichero
 * generado y protege de la misma cadena en futuras consolidaciones.
 *
 * @param {{ from: string, to: string }[]} redirecciones
 * @returns {{ from: string, to: string }[]}
 */
export function aplanarRedirecciones(redirecciones) {
    const mapa = construirMapa(redirecciones);
    return [...mapa.keys()].map((origen) => ({ from: origen, to: destinoFinal(origen, mapa) }));
}

function construirMapa(redirecciones) {
    const mapa = new Map();
    for (const { from, to } of redirecciones) {
        const previo = mapa.get(from);
        if (previo && previo !== to) {
            throw new Error(`Redirección del blog contradictoria: ${from} apunta a ${previo} y a ${to}.`);
        }
        mapa.set(from, to);
    }
    return mapa;
}

function destinoFinal(origen, mapa) {
    const visitados = new Set([origen]);
    let destino = mapa.get(origen);
    while (mapa.has(destino)) {
        if (visitados.has(destino)) {
            throw new Error(`Bucle de redirecciones del blog a partir de ${origen}.`);
        }
        visitados.add(destino);
        destino = mapa.get(destino);
    }
    return destino;
}

/** Todas las redirecciones permanentes de /blog, ya sin cadenas. */
export const REDIRECCIONES_BLOG = aplanarRedirecciones([...BLOG_DEDUPE_REDIRECTS, ...REDIRECCIONES_CONSOLIDACION]);

/**
 * Slugs que redirigen. Un post fusionado sigue publicado en la API hasta que
 * se archiva, y no debe aparecer en el sitemap ni en el listado mientras tanto.
 *
 * @type {ReadonlySet<string>}
 */
export const SLUGS_REDIRIGIDOS = new Set(REDIRECCIONES_BLOG.map(({ from }) => from));
